import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UnifiedLLMService } from '@/services/llm/UnifiedLLMService';
import type { DigcompCompetence, DigcompLevel } from '@/hooks/useDigcompData';

export type DigcompEntityType =
  | 'event'
  | 'podcast'
  | 'course'
  | 'module'
  | 'lesson'
  | 'quiz'
  | 'quiz_question';

export interface DigcompMappingResult {
  competence_id: string;
  area_id: string;
  level_id?: string | null;
}

export interface DigcompCourseMappingResult {
  competences: Array<{
    competence_id: string;
    target_level_id?: string | null;
    weight?: number;
  }>;
}

export interface DigcompMappingContent {
  title?: string;
  topic?: string;
  description?: string;
  what_you_will_learn?: string;
  story_behind?: string;
  goals?: string;
  long_description?: string;
  question_text?: string;
  options?: string[];
  explanation?: string;
  transcript?: string;
  text_content?: string;
  [key: string]: string | string[] | undefined;
}

async function fetchDigcompFramework(brandId: string) {
  const [competencesRes, levelsRes] = await Promise.all([
    supabase
      .from('os_lms_digcomp_competences')
      .select('id, code, name, description, area_id, area:os_lms_digcomp_areas(id, code, name)')
      .eq('brand_id', brandId)
      .eq('is_active', true)
      .order('sort_order'),
    supabase
      .from('os_lms_digcomp_levels')
      .select('id, level_number, name, category, description')
      .eq('brand_id', brandId)
      .eq('is_active', true)
      .order('level_number'),
  ]);

  if (competencesRes.error) throw competencesRes.error;
  if (levelsRes.error) throw levelsRes.error;

  return {
    competences: competencesRes.data as (DigcompCompetence & { area?: { id: string; code: string; name: string } | { id: string; code: string; name: string }[] })[],
    levels: levelsRes.data as DigcompLevel[],
  };
}

function buildSystemPrompt(
  competences: Awaited<ReturnType<typeof fetchDigcompFramework>>['competences'],
  levels: DigcompLevel[]
) {
  const competenceList = competences
    .map((c) => {
      const area = Array.isArray(c.area) ? c.area[0] : c.area;
      return `- ID: ${c.id} | Code: ${c.code} | Name: ${c.name} | Area ID: ${c.area_id}${area ? ` | Area: ${area.name}` : ''}${c.description ? ` | Description: ${c.description}` : ''}`;
    })
    .join('\n');

  const levelList = levels
    .map(
      (l) =>
        `- ID: ${l.id} | Level ${l.level_number}: ${l.name} (${l.category})${l.description ? ` - ${l.description}` : ''}`
    )
    .join('\n');

  return `You are an expert in the DigComp 3.0 European Digital Competence Framework for citizens. Your task is to map educational/training content to the most relevant digital competence(es) and proficiency level(s).

## Available Competences (use ONLY these IDs)
${competenceList}

## Available Proficiency Levels (use ONLY these IDs)
${levelList}

## Output Rules
1. Return ONLY valid JSON, no other text.
2. For single-entity mapping (event, podcast, quiz, quiz_question): return {"competence_id": "<uuid>", "area_id": "<uuid>", "level_id": "<uuid>" or null}
3. For courses, modules, and lessons (can have multiple competences): return {"competences": [{"competence_id": "<uuid>", "target_level_id": "<uuid>" or null, "weight": 1-10}, ...]}
4. For multi-competence mappings, include "weight" (1-10) per competence to indicate relative importance.
5. Use ONLY the IDs listed above - they must exist in the framework.
6. If no suitable match exists, return {"competence_id": null, "area_id": null, "level_id": null} for single entities, or {"competences": []} for courses/modules/lessons.
7. Prefer the most specific competence that matches the content. Include level_id when the content clearly targets a proficiency level.`;
}

function buildUserPrompt(
  entityType: DigcompEntityType,
  content: DigcompMappingContent
) {
  const parts: string[] = [];

  if (content.title) parts.push(`Title: ${content.title}`);
  if (content.topic) parts.push(`Topic: ${content.topic}`);
  if (content.description) parts.push(`Description: ${content.description}`);
  if (content.what_you_will_learn) parts.push(`What you will learn: ${content.what_you_will_learn}`);
  if (content.story_behind) parts.push(`Story behind: ${content.story_behind}`);
  if (content.goals) parts.push(`Goals: ${content.goals}`);
  if (content.long_description) parts.push(`Long description: ${content.long_description}`);
  if (content.transcript) parts.push(`Transcript: ${content.transcript}`);
  if (content.text_content) parts.push(`Content: ${content.text_content}`);
  if (content.question_text) parts.push(`Question: ${content.question_text}`);
  if (content.options?.length) parts.push(`Options: ${content.options.join(' | ')}`);
  if (content.explanation) parts.push(`Explanation: ${content.explanation}`);

  const contentText = parts.length > 0 ? parts.join('\n\n') : '(No content provided)';
  return `Map this ${entityType.replace('_', ' ')} content to DigComp 3.0:

${contentText}

Return the mapping as JSON only.`;
}

function parseLLMResponse(
  content: string,
  entityType: DigcompEntityType
): DigcompMappingResult | DigcompCourseMappingResult | null {
  const trimmed = content.trim();
  const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;

  try {
    const parsed = JSON.parse(jsonMatch[0]);

    if (entityType === 'course' || entityType === 'module' || entityType === 'lesson') {
      if (parsed.competences && Array.isArray(parsed.competences)) {
        return {
          competences: parsed.competences.map((c: any) => ({
            competence_id: c.competence_id || c.competenceId,
            target_level_id: c.target_level_id ?? c.targetLevelId ?? null,
            weight: typeof c.weight === 'number' ? Math.min(10, Math.max(1, c.weight)) : 1,
          })),
        };
      }
      return { competences: [] };
    }

    // Single entity
    const competenceId = parsed.competence_id ?? parsed.competenceId ?? null;
    const areaId = parsed.area_id ?? parsed.areaId ?? null;
    const levelId = parsed.level_id ?? parsed.levelId ?? null;

    if (competenceId && areaId) {
      return { competence_id: competenceId, area_id: areaId, level_id: levelId };
    }
    return null;
  } catch {
    return null;
  }
}

export interface UseDigcompAIMappingParams {
  brandId: string | null;
  entityType: DigcompEntityType;
  entityId?: string | null;
  content: DigcompMappingContent;
}

export function useDigcompAIMapping({
  brandId,
  entityType,
  entityId,
  content,
}: UseDigcompAIMappingParams) {
  return useMutation({
    mutationFn: async (): Promise<DigcompMappingResult | DigcompCourseMappingResult | null> => {
      if (!brandId) {
        throw new Error('Brand ID is required');
      }

      const { competences, levels } = await fetchDigcompFramework(brandId);
      if (competences.length === 0) {
        throw new Error('DigComp framework not seeded. Please seed the framework first.');
      }

      const systemPrompt = buildSystemPrompt(competences, levels);
      const userPrompt = buildUserPrompt(entityType, content);

      const response = await UnifiedLLMService.callLLM({
        brandId,
        source: 'digcomp-ai-mapping',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.2,
        max_tokens: 1024,
      });

      const result = parseLLMResponse(response.content, entityType);
      return result;
    },
  });
}
